import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';
import { ChevronRight, ChevronLeft, Target, TrendingUp, Users, Building, DollarSign, MapPin, Award, ArrowRight, CheckCircle, AlertTriangle, Zap, Star, Package, School, Store, Home, Handshake, Calendar, Clock } from 'lucide-react';

const BlueprintDashboard = () => {
  const [activeSection, setActiveSection] = useState(0);

  const marketData = [
    { name: 'Gerbangkertosusila', population: 5.1, marketSize: 7.8, kabupaten: 8 },
    { name: 'Mataraman', population: 6.8, marketSize: 6.8, kabupaten: 12 },
    { name: 'Tapal Kuda', population: 4.6, marketSize: 5.1, kabupaten: 10 },
    { name: 'Pandalungan', population: 3.5, marketSize: 4.1, kabupaten: 8 }
  ];

  const costComparisonData = [
    { component: 'Production', aqua: 25, vit: 35, nuModel: 30 },
    { component: 'Logistics', aqua: 45, vit: 35, nuModel: 15 },
    { component: 'Marketing', aqua: 20, vit: 15, nuModel: 10 },
    { component: 'Distribution', aqua: 10, vit: 15, nuModel: 45 }
  ];

  const marketShareProjection = [
    { year: 'Year 1', nuShare: 15, aquaImpact: -8, vitImpact: -3, localImpact: -4 },
    { year: 'Year 3', nuShare: 35, aquaImpact: -18, vitImpact: -8, localImpact: -9 },
    { year: 'Year 5', nuShare: 55, aquaImpact: -28, vitImpact: -12, localImpact: -15 }
  ];

  const sections = [
    {
      id: 'overview',
      title: 'Visi Besar: Dominasi AMDK Jawa Timur',
      icon: Target,
      content: (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-2xl">
            <h2 className="text-3xl font-bold mb-4">Paradigma Baru Industri AMDK Indonesia</h2>
            <p className="text-xl opacity-90 mb-6">
              Nahdlatul Ulama dengan 20 juta anggota di Jawa Timur memiliki peluang historis 
              untuk merevolusi industri AMDK melalui strategi territorial dominance berbasis organisasi massa.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold">40.7M</div>
                <div className="text-sm opacity-80">Populasi Jawa Timur</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">20M</div>
                <div className="text-sm opacity-80">Anggota NU</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">Rp 23.8T</div>
                <div className="text-sm opacity-80">Market Size AMDK</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">38</div>
                <div className="text-sm opacity-80">Kabupaten/Kota</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <TrendingUp className="mr-3 text-green-600" />
                Market Opportunity
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Growth Rate:</strong> 8.5% annually (di atas rata-rata nasional 6.2%)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Per Capita:</strong> 84 liter/tahun (meningkat 12% annually)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Economic Leakage:</strong> Rp 720 miliar/bulan keluar dari komunitas NU</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Award className="mr-3 text-blue-600" />
                Competitive Moat
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Star className="mr-3 text-yellow-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>99 tahun</strong> brand equity NU yang tidak dapat dibeli</span>
                </li>
                <li className="flex items-start">
                  <Star className="mr-3 text-yellow-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Struktur organisasi</strong> hingga Ranting yang unik</span>
                </li>
                <li className="flex items-start">
                  <Star className="mr-3 text-yellow-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>12.847 lembaga</strong> pendidikan Maarif sebagai captive market</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'market-potential',
      title: 'Potensi Market Jawa Timur',
      icon: MapPin,
      content: (
        <div className="space-y-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold mb-6">Market Potential per Regional Cluster</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={marketData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value, name) => {
                  if (name === 'population') return [`${value} juta jiwa`, 'Populasi NU'];
                  if (name === 'marketSize') return [`Rp ${value} trilun`, 'Market Size'];
                  return [value, name];
                }} />
                <Bar dataKey="population" fill="#3B82F6" name="population" />
                <Bar dataKey="marketSize" fill="#10B981" name="marketSize" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border-l-4 border-red-500">
              <h4 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                <AlertTriangle className="mr-2" />
                Current Economic Leakage
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Per Bulan:</span>
                  <span className="font-bold text-red-600">Rp 720 miliar</span>
                </div>
                <div className="flex justify-between">
                  <span>Per Tahun:</span>
                  <span className="font-bold text-red-600">Rp 8.64 trilun</span>
                </div>
                <div className="flex justify-between">
                  <span>Dependency Ratio:</span>
                  <span className="font-bold text-red-600">95%</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-l-4 border-green-500">
              <h4 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                <Zap className="mr-2" />
                Economic Multiplier Effect
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Direct Impact:</span>
                  <span className="font-bold text-green-600">Rp 8.64 trilun</span>
                </div>
                <div className="flex justify-between">
                  <span>Job Creation:</span>
                  <span className="font-bold text-green-600">120.000+</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Economic Impact:</span>
                  <span className="font-bold text-green-600">Rp 25.9 trilun</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'structural-weakness',
      title: 'Kelemahan Struktural Industri AMDK',
      icon: AlertTriangle,
      content: (
        <div className="space-y-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold mb-6">Cost Structure Comparison</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={costComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="component" />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}%`, 'Cost Percentage']} />
                <Bar dataKey="aqua" fill="#FF6B6B" name="National Brands" />
                <Bar dataKey="vit" fill="#4ECDC4" name="Local Brands" />
                <Bar dataKey="nuModel" fill="#45B7D1" name="NU Model" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <h4 className="text-lg font-bold text-red-800 mb-3">Logistic Cost Burden</h4>
              <ul className="space-y-2 text-sm">
                <li>• <strong>Aqua:</strong> Rp 450/karton freight cost</li>
                <li>• <strong>Average Distance:</strong> 250 km</li>
                <li>• <strong>Inventory:</strong> 30-45 hari</li>
                <li>• <strong>Breakage:</strong> 3-5%</li>
              </ul>
            </div>

            <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
              <h4 className="text-lg font-bold text-orange-800 mb-3">High Customer Acquisition Cost</h4>
              <ul className="space-y-2 text-sm">
                <li>• <strong>Aqua Marketing:</strong> Rp 2.1 trilun/tahun</li>
                <li>• <strong>CAC:</strong> Rp 125.000/customer</li>
                <li>• <strong>Churn Rate:</strong> 35% annually</li>
                <li>• <strong>ROI:</strong> 1:2.3 (decreasing)</li>
              </ul>
            </div>

            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
              <h4 className="text-lg font-bold text-green-800 mb-3">NU Competitive Advantage</h4>
              <ul className="space-y-2 text-sm">
                <li>• <strong>Freight Cost:</strong> Rp 120/karton (65% reduction)</li>
                <li>• <strong>CAC:</strong> Rp 0 (organic)</li>
                <li>• <strong>Churn Rate:</strong> 5% annually</li>
                <li>• <strong>ROI:</strong> 1:12 (word-of-mouth)</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'strategic-impact',
      title: 'Dampak Strategis Total Dominasi',
      icon: Target,
      content: (
        <div className="space-y-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold mb-6">Market Share Impact Projection</h3>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={marketShareProjection}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="nuShare" stackId="1" stroke="#10B981" fill="#10B981" name="NU Market Share %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border">
              <h4 className="text-xl font-bold mb-4 text-gray-800">Revenue Impact pada Kompetitor</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium">Aqua (Danone)</span>
                  <span className="font-bold text-red-600">-Rp 3.6 trilun (25% drop)</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                  <span className="font-medium">VIT</span>
                  <span className="font-bold text-orange-600">-Rp 1.2 trilun (60% drop)</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="font-medium">Local Brands</span>
                  <span className="font-bold text-yellow-600">-Rp 2.1 trilun (75% loss)</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border">
              <h4 className="text-xl font-bold mb-4 text-gray-800">Supply Chain Disruption</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                  <span>1.200+ distributor kehilangan revenue</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-orange-500 rounded-full mr-3"></div>
                  <span>15.000+ sales force realokasi</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full mr-3"></div>
                  <span>12 major plants underutilization</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                  <span>Price war intensification</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'program-unggulan',
      title: 'Program Unggulan LPNU Lamongan',
      icon: Package,
      content: (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-4">5 Program Strategis Pemberdayaan Ekonomi Keummatan</h3>
            <p className="text-lg opacity-90">
              Program terintegrasi untuk membangun ekosistem ekonomi mandiri dari distribusi, produksi, hingga pembiayaan
            </p>
          </div>

          <div className="grid gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-blue-200">
              <div className="flex items-start">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Package className="text-blue-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-blue-800 mb-3">1. Distribusi Masif AMDK NU Drizce</h4>
                  <p className="text-gray-600 mb-4">
                    Program distribusi Air Minum Dalam Kemasan ke seluruh jaringan 346 Ranting NU sebagai produk andalan komunitas.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">107.260</div>
                      <div className="text-sm text-gray-600">Karton/bulan target</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">Rp 77.8 Juta</div>
                      <div className="text-sm text-gray-600">Kontribusi ke PCNU</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">346</div>
                      <div className="text-sm text-gray-600">Ranting</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-green-200">
              <div className="flex items-start">
                <div className="bg-green-100 p-3 rounded-lg mr-4">
                  <School className="text-green-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-green-800 mb-3">2. Galonisasi Sekolah Maarif & Lembaga NU</h4>
                  <p className="text-gray-600 mb-4">
                    Program penyediaan air galon untuk seluruh unit pendidikan Maarif dan lembaga NU sebagai pasar captive berkelanjutan.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">11.000</div>
                      <div className="text-sm text-gray-600">Galon/bulan</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">696</div>
                      <div className="text-sm text-gray-600">Lembaga target</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">Rp 22 Juta</div>
                      <div className="text-sm text-gray-600">Revenue bulanan</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-purple-200">
              <div className="flex items-start">
                <div className="bg-purple-100 p-3 rounded-lg mr-4">
                  <Star className="text-purple-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-purple-800 mb-3">3. Satu Ranting, Satu Brand, Satu Produk</h4>
                  <p className="text-gray-600 mb-4">
                    Program pengembangan 346 produk lokal unggulan dari 346 Ranting NU sebagai basis ekonomi kreatif.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">346</div>
                      <div className="text-sm text-gray-600">Produk lokal</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">346</div>
                      <div className="text-sm text-gray-600">Merek terdaftar</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">346</div>
                      <div className="text-sm text-gray-600">Usaha Ranting</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-orange-200">
              <div className="flex items-start">
                <div className="bg-orange-100 p-3 rounded-lg mr-4">
                  <Store className="text-orange-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-orange-800 mb-3">4. Distribusi Melalui Toserba Sunan Drajat</h4>
                  <p className="text-gray-600 mb-4">
                    Kemitraan strategis dengan Toserba Sunan Drajat sebagai channel distribusi utama ke pasar yang lebih luas.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-lg font-bold text-orange-600">NU Corner Concept</div>
                      <div className="text-sm text-gray-600">Dedicated section di setiap outlet</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-lg font-bold text-orange-600">70:30 Revenue Split</div>
                      <div className="text-sm text-gray-600">Konsinyasi dengan bagi hasil</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-indigo-200">
              <div className="flex items-start">
                <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                  <Home className="text-indigo-600" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-indigo-800 mb-3">5. Pendanaan Awal Melalui Developer Perumahan</h4>
                  <p className="text-gray-600 mb-4">
                    Skema pendanaan inovatif melalui kemitraan dengan PT Djem Djaya Makmur untuk modal awal LPNU.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-indigo-600">70 Unit</div>
                      <div className="text-sm text-gray-600">Rumah bersubsidi tanpa DP</div>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-indigo-600">Rp 280 Juta</div>
                      <div className="text-sm text-gray-600">Total modal awal</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border">
            <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <Handshake className="mr-3 text-blue-600" />
              Sinergi Program Terintegrasi
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold mb-3 text-gray-700">Ekosistem Distribusi:</h5>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• AMDK sebagai anchor product untuk membangun network</li>
                  <li>• Galonisasi menciptakan recurring revenue institutional</li>
                  <li>• Toserba sebagai gateway ke market yang lebih luas</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-3 text-gray-700">Pemberdayaan Ekonomi:</h5>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• 346 Usaha baru dari program Satu Ranting Satu Produk Satu Merek</li>
                  <li>• Modal awal dari developer untuk sustainability</li>
                  <li>• Network effect yang memperkuat seluruh program</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'timeline-amdk',
      title: 'Timeline Implementasi Dominasi AMDK di Lamongan',
      icon: Calendar,
      content: (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-4">Roadmap Dominasi AMDK Lamongan 2025-2028</h3>
            <p className="text-lg opacity-90">
              Strategi implementasi bertahap untuk menguasai 40% market share Lamongan dan menjadi model untuk 37 PCNU lainnya
            </p>
          </div>

          <div className="grid gap-6">
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Q3 2025 - Persiapan & Sosialisasi</h4>
                  <p className="text-gray-600 mb-2">Foundation Building</p>
                  <div className="flex items-center text-blue-600 mb-3">
                    <Target className="mr-2" size={16} />
                    <span className="font-medium">346 Ranting Tersosialisasi</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Roadshow ke 15 MWC dengan blueprint</li>
                    <li>• MoU LPNU x Developer (70 unit rumah)</li>
                    <li>• Penguatan merek "NU Drizce"</li>
                    <li>• Rekrutment master trainer</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-blue-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Budget:</span>
                    <div className="font-bold text-blue-600">Rp 50 Juta</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Q4 2025 - Pilot Project</h4>
                  <p className="text-gray-600 mb-2">Raelisasi Terbatas</p>
                  <div className="flex items-center text-green-600 mb-3">
                    <Target className="mr-2" size={16} />
                    <span className="font-medium">50 Ranting Aktif + 20 Sekolah</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Realisasi NU Drizce (50 Ranting pilot)</li>
                    <li>• Galonisasi 20 sekolah Maarif</li>
                    <li>• 3 NU Corner di Toserba pilot</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-green-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Target Revenue:</span>
                    <div className="font-bold text-green-600">Rp 235 Juta</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-yellow-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Q1-Q2 2026 - Ekspansi Bertahap</h4>
                  <p className="text-gray-600 mb-2">Scale-Up Distribusi</p>
                  <div className="flex items-center text-yellow-600 mb-3">
                    <Target className="mr-2" size={16} />
                    <span className="font-medium">300 Ranting Aktif (Q2)</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Q1: 150 Ranting distribusi Drizce</li>
                    <li>• Q2: 300 Ranting aktif (87% coverage)</li>
                    <li>• 200 sekolah galonisasi aktif</li>
                    <li>• Penetrasi ke Bojonegoro, Tuban, Gresik</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-yellow-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Market Share:</span>
                    <div className="font-bold text-yellow-600">25%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Q3-Q4 2026 - Konsolidasi</h4>
                  <p className="text-gray-600 mb-2">Standardisasi & Optimalisasi</p>
                  <div className="flex items-center text-purple-600 mb-3">
                    <Target className="mr-2" size={16} />
                    <span className="font-medium">346 Ranting Full Coverage</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Semua 346 Ranting aktif distribusi</li>
                    <li>• Sertifikasi halal semua produk</li>
                    <li>• NU Corner di seluruh outlet Toserba</li>
                    <li>• Quality control system terintegrasi</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-purple-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Target Revenue:</span>
                    <div className="font-bold text-purple-600">Rp 1.46M</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">2027 - Market Leadership</h4>
                  <p className="text-gray-600 mb-2">Dominasi & Institusionalisasi</p>
                  <div className="flex items-center text-red-600 mb-3">
                    <Target className="mr-2" size={16} />
                    <span className="font-medium">40% Market Share Lamongan</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Market leader AMDK di Lamongan</li>
                    <li>• Ekspansi ke 3 kabupaten tetangga</li>
                    <li>• Pemanfaatan Platform E Commerce</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-red-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Valuasi:</span>
                    <div className="font-bold text-red-600">Rp 10M</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
            <h4 className="text-xl font-bold text-blue-800 mb-4">Lamongan sebagai Proof of Concept</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold mb-2 text-blue-700">Success Metrics untuk Replikasi:</h5>
                <ul className="space-y-1 text-sm text-blue-600">
                  <li>• 40% market share dalam 24 bulan</li>
                  <li>• 15% net margin sustainable</li>
                  <li>• 1.200+ job creation local</li>
                  <li>• ROI 268% dalam 3 tahun</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-2 text-blue-700">Template untuk 37 PCNU Lain:</h5>
                <ul className="space-y-1 text-sm text-blue-600">
                  <li>• Standardized business model</li>
                  <li>• Scalable training curriculum</li>
                  <li>• Shared technology platform</li>
                  <li>• Proven financing structure</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'timeline-ranting',
      title: 'Timeline Implementasi Program "Satu Ranting, Satu Produk dan Satu Brand"',
      icon: Clock,
      content: (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-4">Roadmap Pemberdayaan 346 Usaha Ranting</h3>
            <p className="text-lg opacity-90">
              Program inkubasi bertahap untuk menciptakan 346 produk unggulan lokal dengan brand terdaftar dan sustainable
            </p>
          </div>

          <div className="grid gap-6">
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-indigo-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Phase 1: Pemetaan & Seleksi (Q4 2025)</h4>
                  <p className="text-gray-600 mb-2">Foundation Assessment</p>
                  <div className="flex items-center text-indigo-600 mb-3">
                    <Users className="mr-2" size={16} />
                    <span className="font-medium">346 Ranting Survey + 50 Ranting Pilot</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Survey komprehensif potensi tiap ranting</li>
                    <li>• Identifikasi SDM dengan keahlian spesifik</li>
                    <li>• Assessment kultur dan tradisi marketable</li>
                    <li>• Seleksi 50 Ranting untuk Batch 1 inkubasi</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-indigo-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Budget:</span>
                    <div className="font-bold text-indigo-600">Rp 75 Juta</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Phase 2: Inkubasi Batch 1 (Q1 2026)</h4>
                  <p className="text-gray-600 mb-2">Pilot Batch Development</p>
                  <div className="flex items-center text-blue-600 mb-3">
                    <Package className="mr-2" size={16} />
                    <span className="font-medium">50 Produk Prototype Siap</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Pelatihan intensif 5 modul (2 minggu)</li>
                    <li>• Product development & prototyping</li>
                    <li>• Legalitas usaha & sertifikasi</li>
                    <li>• Brand development & packaging design</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-blue-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Target:</span>
                    <div className="font-bold text-blue-600">50 Ranting</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Phase 3: Scale-Up Batch 2-4 (Q2-Q4 2026)</h4>
                  <p className="text-gray-600 mb-2">Massive Rollout</p>
                  <div className="flex items-center text-green-600 mb-3">
                    <Building className="mr-2" size={16} />
                    <span className="font-medium">200 Produk Launched (Kumulatif)</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Q2: Batch 2 (75 Ranting) + Launching Batch 1</li>
                    <li>• Q3: Batch 3 (75 Ranting) + Market penetration</li>
                    <li>• Q4: Batch 4 (75 Ranting) + Quality standardization</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-green-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Revenue Target:</span>
                    <div className="font-bold text-green-600">Rp 180 Juta</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-yellow-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Phase 4: Completion (Q1-Q2 2027)</h4>
                  <p className="text-gray-600 mb-2">Final Wave & Optimization</p>
                  <div className="flex items-center text-yellow-600 mb-3">
                    <Star className="mr-2" size={16} />
                    <span className="font-medium">346 Produk Complete Portfolio</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Q1: Batch 5 (71 Ranting terakhir)</li>
                    <li>• Q2: Full portfolio 346 produk aktif</li>
                    <li>• Advanced training program (Gold certification)</li>
                    <li>• Penilaian kesiapan ekspor</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-yellow-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Target Revenue:</span>
                    <div className="font-bold text-yellow-600">Rp 400 Juta</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800">Phase 5: Sustainability (Q3-Q4 2027)</h4>
                  <p className="text-gray-600 mb-2">Self-Sustaining Ecosystem</p>
                  <div className="flex items-center text-purple-600 mb-3">
                    <Award className="mr-2" size={16} />
                    <span className="font-medium">346 Ranting Mandiri</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Graduation program untuk 346 Ranting</li>
                    <li>• Advanced marketplace features (B2B, Export)</li>
                    <li>• Peer-to-peer mentoring network</li>
                    <li>• Regional expansion ke 3 kabupaten</li>
                  </ul>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6">
                  <div className="bg-purple-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Graduation Rate:</span>
                    <div className="font-bold text-purple-600">85%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-green-100 p-6 rounded-xl border border-green-200">
              <h4 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                <CheckCircle className="mr-2" />
                Success Metrics Program
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Produk Launched:</span>
                  <span className="font-bold text-green-600">346 produk</span>
                </div>
                <div className="flex justify-between">
                  <span>Brand Terdaftar:</span>
                  <span className="font-bold text-green-600">346 merek</span>
                </div>
                <div className="flex justify-between">
                  <span>Ranting Mandiri:</span>
                  <span className="font-bold text-green-600">294 Ranting (85%)</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Investment:</span>
                  <span className="font-bold text-green-600">Rp 500 Juta</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <h4 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                <TrendingUp className="mr-2" />
                Economic Impact
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Direct Jobs:</span>
                  <span className="font-bold text-blue-600">1.200+ jobs</span>
                </div>
                <div className="flex justify-between">
                  <span>Economic Activity:</span>
                  <span className="font-bold text-blue-600">Rp 2.4M annually</span>
                </div>
                <div className="flex justify-between">
                  <span>Export Potential:</span>
                  <span className="font-bold text-blue-600">50+ produk</span>
                </div>
                <div className="flex justify-between">
                  <span>ROI Program:</span>
                  <span className="font-bold text-blue-600">480% (3 tahun)</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-xl border">
            <h4 className="text-xl font-bold text-gray-800 mb-4">Modul Pelatihan Berlapis</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-semibold text-gray-700 mb-2">Level 1: Mindset (1 hari)</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Filosofi ekonomi Islam NU</li>
                  <li>• Konsep berkah berbisnis</li>
                  <li>• Gotong royong advantage</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-semibold text-gray-700 mb-2">Level 2: Operations (4 hari)</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Business planning</li>
                  <li>• Financial management</li>
                  <li>• Quality assurance</li>
                  <li>• Legal compliance</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-semibold text-gray-700 mb-2">Level 3: Marketing (4 hari)</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Brand development</li>
                  <li>• Digital marketing</li>
                  <li>• E-commerce platforms</li>
                  <li>• Export preparation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentSection = sections[activeSection];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="bg-white shadow-lg border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Blueprint Strategis LPNU Lamongan</h1>
              <p className="text-lg text-gray-600 mt-2">Dominasi Industri Air Minum Dalam Kemasan Melalui Network NU</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">2025-2027</div>
              <div className="text-lg font-semibold text-blue-600">Leader Project</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-80 space-y-2">
            <div className="bg-white rounded-xl shadow-lg p-4 sticky top-4">
              <h3 className="font-bold text-gray-800 mb-4">Navigasi Blueprint</h3>
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(index)}
                    className={`w-full flex items-center p-3 rounded-lg text-left transition-all ${
                      activeSection === index
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <Icon size={20} className="mr-3 flex-shrink-0" />
                    <span className="font-medium">{section.title}</span>
                    {activeSection === index && <ChevronRight size={16} className="ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-8">
                <currentSection.icon size={32} className="mr-4 text-blue-600" />
                <h2 className="text-3xl font-bold text-gray-900">{currentSection.title}</h2>
              </div>
              
              {currentSection.content}

              <div className="flex justify-between mt-12 pt-8 border-t border-gray-200">
                <button
                  onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                  disabled={activeSection === 0}
                  className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                    activeSection === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <ChevronLeft size={20} className="mr-2" />
                  Sebelumnya
                </button>

                <button
                  onClick={() => setActiveSection(Math.min(sections.length - 1, activeSection + 1))}
                  disabled={activeSection === sections.length - 1}
                  className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                    activeSection === sections.length - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                  }`}
                >
                  Selanjutnya
                  <ChevronRight size={20} className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-xl font-bold mb-2">LPNU Kabupaten Lamongan</h3>
          <p className="text-gray-400 mb-4">Menggerakkan Ekonomi Keummatan Melalui Ekosistem Distribusi, Inkubasi UMKM, dan Kemitraan Strategis</p>
          <div className="text-sm text-gray-500">
            Disusun oleh Tim Strategis LPNU Lamongan • Juli 2025
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlueprintDashboard;
